import { Tooltip } from '@material-ui/core';
import CSS from 'csstype';
import { cloneDeep, isEmpty, pick } from 'lodash-es';
import moment from 'moment';
import React from 'react';

import { getSmallSites } from '~/src/dash/api/channels/channels_methods';
import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import { HeaderBidderActiveActive, IUser } from '~/src/dash/api/console-api';
import {
  DashboardGraphID,
  IDashboardChartData,
  IDashboardChartDataKey,
  IDashboardHomeCardMetricKey,
  IDashboardHomeCardMetrics,
} from '~/src/dash/api/dashboards/home';
import { querySmallBidders } from '~/src/dash/api/filters/bidders';
import { getFilteredDemandSourcesWithBidderTypes } from '~/src/dash/api/filters/demandSources';
import { getInventoryRoutesByPublisherID, IInventoryRoute } from '~/src/dash/api/inventory_routes';
import { EActive } from '~/src/dash/api/types/active';
import { IDashboardCard } from '~/src/dash/components/cards/common';
import {
  IDashboardChartMetric,
  IDashboardSubChartMetric,
} from '~/src/dash/components/charts/common';
import { FilterPillKey, ICustomAPIFilterConfig } from '~/src/dash/components/filters/common';
import { TrendMiniIcon } from '~/src/dash/components/icons/misc-icons';
import { TKey, usePublicaTranslation } from '~/src/dash/publicaUI';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { EDateGroup } from '~/src/dash/utils/dateGroups';
import { IDateRange } from '~/src/dash/utils/dateTimeRanges';
import { checkUserInfoMasked } from '~/src/dash/utils/roles';
import { IDashboardHomeCardsConfig } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardOverviewConfig';
import { ICSVColumn } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardOverview/CSVDownloadButton';
import { IMobileDashboardHomeCardsConfig } from '~/src/dash/views/mobile/MobileBaseView/Home/configs/overviewConfig';

// IMPORTANT!!!
// THIS FILE SHARES EXPORTS WITH MOBILE DASHBOARD
// CHECK THOROUGHLY BEFORE ALTERING

export interface IActiveFilters {
  demand: string;
  supply: string;
  device: string;
  deal: string;
  publisher: string;
  cluster: string;
  region: string;
  domain: string;
  requester: string;
}

export const clearedActiveFilters: IActiveFilters = {
  demand: '',
  supply: '',
  device: '',
  deal: '',
  publisher: '',
  cluster: '',
  region: '',
  domain: '',
  requester: '',
};

export const addDefaultSelected = (
  filterKeys: FilterPillKey[],
  updatedSelectedFilters: Record<FilterPillKey, IMenuList>,
  updatedFilters: Record<FilterPillKey, IMenuList>,
  cfg: ICustomAPIFilterConfig,
  areAllPubsActive?: boolean,
) => {
  filterKeys.forEach((key) => {
    if (key === 'Publishers' && areAllPubsActive) return;
    if (!isEmpty(cfg[key].defaultSelected)) {
      const defaultSelected = cfg[key].defaultSelected || [];
      defaultSelected.forEach((id) => {
        if (!updatedFilters[key][id].isDisabled) {
          updatedFilters[key][id].selected = true;
        }
        if (updatedSelectedFilters[key]) {
          updatedSelectedFilters[key][id] = updatedFilters[key][id];
        } else {
          updatedSelectedFilters[key] = {
            [id]: updatedFilters[key][id],
          };
        }
      });
    }
  });
};

export const updateDefaultSelectedForFilters = (
  selectedFilters: Record<string, IMenuList>,
  filtersCfg: ICustomAPIFilterConfig,
  defaultStateCfg: Record<string, any>,
) => {
  if (isEmpty(selectedFilters)) {
    Object.values(filtersCfg).forEach((filter) => {
      filter.defaultSelected = [];
    });
  } else {
    Object.keys(selectedFilters).forEach((key) => {
      if (key !== 'Timezones') {
        if (key === 'Filtered Demand Sources') {
          const isUsingBidderType = isBidderTypeSelected(
            selectedFilters['Filtered Demand Sources'],
          );
          if (isUsingBidderType) {
            defaultStateCfg.isDemandSourceUsingBidderType = true;
            const bidderIDsMap: Record<number, boolean> = {};
            Object.values(selectedFilters['Filtered Demand Sources']).forEach((item) => {
              (item.bidderIDs || []).forEach((bidderID) => {
                bidderIDsMap[bidderID] = true;
              });
            });
            filtersCfg[key].defaultGroupSelected = Object.keys(bidderIDsMap);
          } else {
            defaultStateCfg.isDemandSourceUsingBidderType = false;
          }
        }
        filtersCfg[key].defaultSelected = Object.keys(selectedFilters[key]);
      }
    });
  }
};

interface ITrendStickerProps {
  isActive: boolean;
  style?: CSS.Properties;
  onClick?: () => void;
}

export const DashboardTrendsSticker = ({ isActive, onClick, style = {} }: ITrendStickerProps) => {
  const { t } = usePublicaTranslation();
  return (
    <Tooltip title={t('DashboardTooltipComparisonData')} placement='top'>
      <div
        onClick={onClick}
        style={{
          borderRadius: '5px',
          backgroundColor: `${isActive ? publicaColors.honeydew : publicaColors.veryLightGray}`,
          padding: '0px 4px',
          display: 'flex',
          cursor: 'pointer',
          ...style,
        }}
      >
        <TrendMiniIcon color={publicaColors.darkGray} />
      </div>
    </Tooltip>
  );
};

// this function should only be used when a new graph is selected in order to update the cards
export const updateDisplayedMetricKeys = (
  availableMetricKeys: string[],
  currentDisplayedMetricKeys: IDashboardHomeCardMetricKey[],
): IDashboardHomeCardMetricKey[] => {
  const clonedAvailableKeys = [...availableMetricKeys];
  const updatedKeys = [] as IDashboardHomeCardMetricKey[];
  // find and remove duplicate metrics from the available to decrease the pool to only eligible and no repeats
  currentDisplayedMetricKeys.forEach((key) => {
    const keyIndex = clonedAvailableKeys.indexOf(key);
    if (keyIndex > -1) {
      clonedAvailableKeys.splice(keyIndex, 1);
    }
  });
  // second replace previous ineligible metrics with possible metrics from the new pool
  let i = 0;
  currentDisplayedMetricKeys.forEach((key) => {
    if (availableMetricKeys.indexOf(key) > -1) {
      updatedKeys.push(key);
    } else {
      updatedKeys.push(clonedAvailableKeys[i] as keyof IDashboardHomeCardMetrics);
      i++;
    }
  });
  // failsafe check to make sure there are 6
  if (updatedKeys.length < 6) {
    while (updatedKeys.length < 6) {
      updatedKeys.push(clonedAvailableKeys[i] as keyof IDashboardHomeCardMetrics);
      i++;
    }
  }
  return updatedKeys;
};

export const updateActiveChartMetrics = (
  displayedMetricKeys: (keyof IDashboardHomeCardMetrics)[],
  activeChartMetrics: IDashboardChartMetric[],
  cardMetricsCfg: Record<IDashboardHomeCardMetricKey, IDashboardCard>,
) => {
  const updatedActiveChartMetrics = [] as IDashboardChartMetric[];
  activeChartMetrics.forEach((metric) => {
    const metricKey = metric.key as keyof IDashboardHomeCardMetrics;
    const cardIndex = displayedMetricKeys.indexOf(metricKey);
    if (cardIndex > -1) {
      metric.chartColorIndex = cardIndex;
      updatedActiveChartMetrics.push(metric);
    }
    if (isEmpty(updatedActiveChartMetrics)) {
      const newMetricKey = displayedMetricKeys[0];
      const { label, valueType, chartType } =
        cardMetricsCfg[newMetricKey as keyof IDashboardHomeCardMetrics];
      updatedActiveChartMetrics.push({
        key: newMetricKey,
        label,
        valueType,
        chartColorIndex: 0,
        chartType,
      });
    }
  });
  return updatedActiveChartMetrics;
};

export type DashboardLiveCardGroup = 'demandSelected' | 'noDemandSelected';

export const getLiveCardGroup = (activeFilters: IActiveFilters): DashboardLiveCardGroup => {
  return !!activeFilters.demand || !!activeFilters.deal ? 'demandSelected' : 'noDemandSelected';
};

export interface IDashboardLiveCards {
  groupings: Record<DashboardLiveCardGroup, string[]>;
  superUserGroupings?: Record<DashboardLiveCardGroup, string[]>;
  samsungAdjustedMetrics?: Record<DashboardLiveCardGroup, string[]>;
}

export const extractViableMetrics = (
  cfg: IDashboardHomeCardsConfig | IMobileDashboardHomeCardsConfig,
  graphID: DashboardGraphID,
): Partial<Record<IDashboardHomeCardMetricKey, IDashboardCard>> => {
  const viableMetrics: Partial<Record<IDashboardHomeCardMetricKey, IDashboardCard>> = {} as Record<
    IDashboardHomeCardMetricKey,
    IDashboardCard
  >;
  const viableKeys = cfg.groupings[graphID];
  viableKeys.forEach((key) => {
    viableMetrics[key as IDashboardHomeCardMetricKey] =
      cfg.metrics[key as IDashboardHomeCardMetricKey];
  });
  return viableMetrics;
};

export const extractViableLiveMetrics = (cfg: IDashboardHomeCardsConfig, viableKeys: string[]) => {
  const viableMetrics = {} as Record<IDashboardHomeCardMetricKey, IDashboardCard>;
  viableKeys.forEach((key) => {
    viableMetrics[key as IDashboardHomeCardMetricKey] =
      cfg.metrics[key as IDashboardHomeCardMetricKey];
  });
  return viableMetrics;
};

const subChartColors = {
  magenta: '#D30C7B',
  olive: '#B2E061',
  blueGray: '#84BCEF',
  orange: '#FF9900',
  neonPink: '#FF66FF',
  purple: '#A34FC2',
  seaGreen: '#85FFC7',
  sienna: '#E88160',
};

const disableIfDemandSelected = (activeFilters: IActiveFilters) => {
  return !!activeFilters.demand || !!activeFilters.deal;
};

const disableIfNotDemandSource = (activeFilters: IActiveFilters) => {
  return activeFilters.demand !== 'Filtered Demand Sources';
};

const defaultNotDisabled = () => false;

export type DashboardSubChartMetrics = Record<IDashboardChartDataKey, IDashboardSubChartMetric>;

export interface IDashboardSubChart {
  title: string;
  tKey?: TKey;
  metrics: Partial<DashboardSubChartMetrics>;
  orderedMetricKeys: IDashboardChartDataKey[];
}

export const keyLatencyMetrics = {
  EndpointLatency: {
    key: 'EndpointLatency',
    label: 'Endpoint Latency',
    responseKey: 'EndpointLatency',
    valueType: 'latency',
    color: subChartColors.blueGray,
    isDisabled: disableIfDemandSelected,
  },
  AuctionLatency: {
    key: 'AuctionLatency',
    label: 'Auction Latency',
    responseKey: 'AuctionLatency',
    valueType: 'latency',
    color: subChartColors.orange,
    isDisabled: disableIfDemandSelected,
  },
  BidderLatency: {
    key: 'BidderLatency',
    label: 'Bidder Latency',
    responseKey: 'BidderLatency',
    valueType: 'latency',
    color: subChartColors.magenta,
    isDisabled: defaultNotDisabled,
  },
} as Partial<DashboardSubChartMetrics>;

export const clientKeyLatencyMetrics = {
  CreationLatency: {
    key: 'CreationLatency',
    label: 'Creation Latency',
    responseKey: 'CreationLatency',
    valueType: 'latency',
    color: subChartColors.purple,
    isDisabled: disableIfNotDemandSource,
    yScale: 'small',
  },
  WaitLatency: {
    key: 'WaitLatency',
    label: 'Wait Latency',
    responseKey: 'WaitLatency',
    valueType: 'latency',
    color: subChartColors.seaGreen,
    isDisabled: disableIfNotDemandSource,
    yScale: 'small',
  },
} as Partial<DashboardSubChartMetrics>;

export const bidErrorsMetrics = {
  BidErrors: {
    key: 'BidErrors',
    label: 'Bid Errors',
    responseKey: 'BidErrors',
    valueType: 'whole_number',
    color: subChartColors.olive,
    isDisabled: defaultNotDisabled,
  },
  BidTimeouts: {
    key: 'BidTimeouts',
    label: 'Bid Timeouts',
    responseKey: 'BidTimeouts',
    valueType: 'whole_number',
    color: subChartColors.neonPink,
    isDisabled: defaultNotDisabled,
  },
} as Partial<DashboardSubChartMetrics>;

export const clientBidErrorsMetrics = {
  BidSchedulingIssues: {
    key: 'BidSchedulingIssues',
    label: 'Bid Scheduling Issues',
    responseKey: 'BidSchedulingIssues',
    valueType: 'whole_number',
    color: subChartColors.sienna,
    isDisabled: disableIfNotDemandSource,
    yScale: 'small',
  },
} as Partial<DashboardSubChartMetrics>;

export const extractDataForMultiLineChart = (data: IDashboardChartData[], metricKeys: string[]) => {
  return data.map((item) => {
    return pick(item, ['Date', ...metricKeys]);
  });
};

export const generateCustomDateRange = (numDays: number): IDateRange => {
  let defaultDateGroup;
  let dateGroupRange;
  switch (true) {
    case numDays >= 27:
      defaultDateGroup = EDateGroup.ByMonth;
      dateGroupRange = [EDateGroup.ByDay, EDateGroup.ByMonth];
      break;
    case numDays >= 6:
      defaultDateGroup = numDays >= 13 ? EDateGroup.ByWeek : EDateGroup.ByDay;
      dateGroupRange = [EDateGroup.ByDay, EDateGroup.ByWeek];
      break;
    default:
      defaultDateGroup = numDays > 0 ? EDateGroup.ByDay : EDateGroup.ByHour;
      dateGroupRange = [EDateGroup.ByHour, EDateGroup.ByDay];
      break;
  }
  return {
    key: 'Custom',
    label: 'Custom',
    shortLabel: 'Custom',
    defaultDateGroup,
    dateGroupRange,
  } as unknown as IDateRange;
};

export const generateInventoryRoutesAndDestinations = (publisherID: string, user: IUser) => {
  return getInventoryRoutesByPublisherID(publisherID).then((inventoryRoutes: IInventoryRoute[]) => {
    const routes: IMenuList = {};
    const destinations: IMenuList = {};
    for (const inventoryRoute of inventoryRoutes) {
      const siteIDs: string[] = [];
      for (const destination of inventoryRoute.Destinations) {
        // destinations without a site do not have data in our dash
        if (!destination.SiteID) continue;
        siteIDs.push(String(destination.SiteID));
        destinations[destination.ID] = {
          title: checkUserInfoMasked(user)
            ? `demo_destination_${destination.ID}`
            : destination.Name,
          selected: false,
          ID: String(destination.ID),
          siteID: destination.SiteID ? String(destination.SiteID) : '',
        };
      }
      // a route could be filled with destinations with no site
      if (siteIDs.length === 0) continue;
      routes[String(inventoryRoute.ID)] = {
        title: checkUserInfoMasked(user)
          ? `demo_inventory_route_${inventoryRoute.ID}`
          : inventoryRoute.Name,
        selected: false,
        ID: String(inventoryRoute.ID),
        siteIDs,
      };
    }
    return {
      Routes: routes,
      Destinations: destinations,
    };
  });
};

export const generateCustomCompareDates = (startDate: moment.Moment, endDate: moment.Moment) => {
  const weeksOffset = endDate.diff(startDate, 'days') > 28 ? 52 : 4;
  return {
    compStartDate: startDate.clone().subtract(weeksOffset, 'weeks'),
    compEndDate: endDate.clone().subtract(weeksOffset, 'weeks'),
  };
};

export const refreshBiddersFilter = async (
  session: ISessionState,
  user: IUserState,
  selectedBidders: IMenuList,
  siteIDs: string,
) => {
  const result = await querySmallBidders(session.publisher.ID, {
    publisher_id: session.publisher.ID,
    site_ids: siteIDs,
  });
  const biddersList: IMenuList = {};
  result.HeaderBidders.forEach((hb) => {
    const ID = hb.ID;
    const title = checkUserInfoMasked(user.user) ? `demo_bidder_${ID}` : hb.Name;
    biddersList[ID] = {
      title,
      selected: false,
      ID,
      active: hb.Active === HeaderBidderActiveActive,
    };
  });
  const refreshedSelectedBidders = {} as IMenuList;
  if (!isEmpty(selectedBidders)) {
    Object.keys(selectedBidders).forEach((id) => {
      if (biddersList[id]) {
        biddersList[id].selected = true;
        refreshedSelectedBidders[id] = biddersList[id];
      }
    });
  }
  return {
    selectedBidders: refreshedSelectedBidders,
    allBidders: biddersList,
  };
};

export const refreshDemandSourcesFilter = async (
  session: ISessionState,
  user: IUserState,
  selectedDemandSources: IMenuList,
  siteIDs: string,
  isBidderTypeDisabled?: boolean,
) => {
  const result = await getFilteredDemandSourcesWithBidderTypes(
    session,
    user,
    siteIDs,
    !!isBidderTypeDisabled,
  );
  const filteredDemandSources = result['Filtered Demand Sources'];
  const refreshedSelectedDemandSources = {} as IMenuList;
  if (!isEmpty(selectedDemandSources)) {
    Object.keys(selectedDemandSources).forEach((id) => {
      if (
        filteredDemandSources[id] &&
        !(isBidderTypeDisabled && filteredDemandSources[id].isDisabled)
      ) {
        filteredDemandSources[id].selected = true;
        refreshedSelectedDemandSources[id] = filteredDemandSources[id];
      }
    });
  }
  return {
    selectedDemandSources: refreshedSelectedDemandSources,
    allDemandSources: filteredDemandSources,
  };
};

export const isBidderTypeSelected = (selectedDemandSources: IMenuList) => {
  return Object.values(selectedDemandSources).some((demandSource) => demandSource.isCustom);
};

export const numSitesBidders = (bidderIDs: string, siteIDs: string) => {
  const bidderCount = bidderIDs.split(',').length || 1;
  const siteCount = siteIDs.split(',').length || 1;
  return bidderCount * siteCount;
};

export const generateCSVColumns = (
  metricKeys: (keyof IDashboardHomeCardMetrics)[],
  metricsConfig: Record<keyof IDashboardHomeCardMetrics, IDashboardCard>,
) => {
  const columns: ICSVColumn[] = [
    {
      id: 'Date',
      displayName: 'Date',
    },
  ];
  metricKeys.forEach((key: keyof IDashboardHomeCardMetrics) => {
    columns.push({
      id: key,
      displayName: metricsConfig[key].label,
    });
  });
  return columns;
};

const formatCSVDate = (date: string, grouping: number) => {
  if (!date) return '';
  if (grouping > 0) {
    return moment(date).utc().format('ddd MM/DD/YY');
  } else {
    return moment(date).utc().format('HH:mm (ddd MM/DD/YY)');
  }
};

export const formatDataToCSV = (
  mainData: IDashboardChartData[],
  compareData: IDashboardChartData[],
  isCompareEnabled: boolean,
  grouping: number,
) => {
  const formattedData: IDashboardChartData[] = [];
  const clonedMainData = cloneDeep(mainData);
  if (isCompareEnabled && !isEmpty(compareData)) {
    const clonedCompareData = cloneDeep(compareData);
    clonedCompareData.forEach((item) => {
      item.Date = formatCSVDate(item.Date, grouping);
      formattedData.push(item);
    });
  }
  clonedMainData.forEach((item) => {
    item.Date = formatCSVDate(item.Date, grouping);
    formattedData.push(item);
  });
  return Promise.resolve(formattedData);
};

export const generateCSVTitle = (filterParams: Record<string, string>) => {
  const paramsString: string[] = Object.entries(filterParams || {}).map(
    ([key, value]) => `${key}: ${value}`,
  );
  return paramsString.join('\u2028');
};

export const refreshChannelsFilter = async (publisherID: string, user: IUserState) => {
  // get sites
  const [small_sites, err] = await getSmallSites(publisherID);
  if (err) {
    console.error(err);
    return {} as IMenuList;
  }
  if (small_sites !== undefined) {
    const siteList: IMenuList = {};
    small_sites.forEach((site: ISmallSite) => {
      const siteName = checkUserInfoMasked(user.user) ? `demo_site_${site.ID}` : site.Name;
      siteList[site.ID] = {
        title: siteName,
        selected: false,
        ID: site.ID.toString(),
        active: site.Active === EActive.ACTIVE,
      };
    });
    return siteList;
  }
  return {} as IMenuList;
};
